// =============================================================================
// RecentCheckInsWidget — live feed of voltage readings across the lot.
//
// Flattens every unit's history into one timeline, newest first. Each entry
// shows who, when, what they did (action), and the reading. Click any
// entry to open that unit's check-in screen.
// =============================================================================
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBatteryUnits, recentActivity, relativeWhen, unitName, authorName } from "../lib/useBatteryData.js";
import { useAuth } from "../auth/AuthProvider.jsx";

export function RecentCheckInsWidget({ config }) {
  const nav = useNavigate();
  const { profile } = useAuth();
  const limit = config?.limit || 12;
  const { data: units, loading } = useBatteryUnits();
  const events = useMemo(() => recentActivity(units, limit), [units, limit]);
  // Self-override — if an entry's author uid matches the current user, we
  // use their CURRENT displayName instead of the snapshot. Fixes historical
  // entries written before displayName was a field.
  const authorOpts = useMemo(
    () => ({ self: { uid: profile?.uid, displayName: profile?.displayName } }),
    [profile?.uid, profile?.displayName]
  );

  return (
    <div className="w-shell">
      <header className="w-head">
        <div className="w-eye"><span className="w-eye-dot" />OPS LOG · LIVE</div>
        <h3 className="w-h3">Every <em>reading</em>, every <em>action</em>.</h3>
      </header>

      {loading && <div className="w-loading">Loading the log…</div>}

      {!loading && events.length === 0 && (
        <div className="w-empty">
          <div className="w-empty-h">No activity yet.</div>
          <div className="w-empty-s">Once units start getting checked, every reading lands here in real time.</div>
        </div>
      )}

      {!loading && events.length > 0 && (
        <div className="w-act">
          {events.map((e, i) => (
            <button
              key={`${e.unitId}-${i}`}
              type="button"
              className="w-act-row"
              style={{ background: "transparent", border: 0, padding: "9px 0", borderBottom: "1px dashed var(--line)", textAlign: "left", width: "100%", cursor: "pointer" }}
              onClick={() => nav(`/check-in/${e.unitId}`)}
            >
              <div className="w-act-when">{relativeWhen(e.at)}</div>
              <div className="w-act-what">
                <b>{authorName(e.by, authorOpts)}</b>
                {Number.isFinite(Number(e.voltage)) && (
                  <> · <span style={{ fontVariantNumeric: "tabular-nums", color: voltageColor(Number(e.voltage)), fontWeight: 600 }}>{Number(e.voltage).toFixed(2)}V</span></>
                )}
                {" "}{actionLabel(e.action)} {e.stockNumber || unitName(e)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function actionLabel(action) {
  const a = String(action || "").toLowerCase();
  if (a.includes("charge")) return "started charge on";
  if (a.includes("remove")) return "removed from charger";
  if (a.includes("intake") || a.includes("add")) return "added";
  if (a.includes("note") || a.includes("comment")) return "noted on";
  return "checked";
}
function voltageColor(v) {
  if (!Number.isFinite(v)) return "var(--text-muted)";
  if (v < 12.0) return "#7C2935";
  if (v < 12.4) return "#B05E13";
  return "#2A6D38";
}
